import { useEffect } from "react";
import { PhoneOff, Phone, Mic, MicOff, PhoneIncoming } from "lucide-react";

function formatDuration(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function stateLabel(state, isCallerFriendlyLabel) {
  switch (state) {
    case "ringing_out": return "جاري الاتصال...";
    case "ringing_in":  return "مكالمة واردة";
    case "connecting":  return "جاري الاتصال...";
    case "active":      return "متصل الآن";
    case "ended":       return "انتهت المكالمة";
    case "error":       return "تعذّر الاتصال";
    default:            return "";
  }
}

/** Full-screen call modal. Rendered globally by VoiceCallProvider. */
export default function VoiceCallModal({ call }) {
  const { state, incoming, durationSec, muted, error,
          acceptIncoming, reject, hangup, toggleMute, remoteAudioRef } = call;

  // Play a subtle ring tone (short beep loop) on incoming/outgoing ringing states.
  // Uses the WebAudio API — no external asset needed.
  useEffect(() => {
    if (state !== "ringing_in" && state !== "ringing_out") return;
    let ctx, osc, gain, interval;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      ctx = new AC();
      const beep = () => {
        try {
          osc = ctx.createOscillator();
          gain = ctx.createGain();
          osc.type = "sine";
          osc.frequency.value = state === "ringing_in" ? 880 : 520;
          gain.gain.setValueAtTime(0.0001, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.14, ctx.currentTime + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
          osc.connect(gain).connect(ctx.destination);
          osc.start();
          osc.stop(ctx.currentTime + 0.4);
        } catch { /* ignore */ }
      };
      beep();
      interval = setInterval(beep, 1500);
    } catch { /* ignore */ }
    return () => {
      try { clearInterval(interval); } catch { /* ignore */ }
      try { ctx && ctx.close(); } catch { /* ignore */ }
    };
  }, [state]);

  // Autoplay retry: browsers block .play() until a user gesture. Once the
  // user taps Accept (state → connecting/active), retry play() so we don't
  // stay silently muted. Also re-attach the stream defensively — if the
  // hook's ontrack fired before the <audio> was mounted, srcObject may be
  // unset even though we have a stream ready.
  useEffect(() => {
    if (state === "active" || state === "connecting") {
      const el = remoteAudioRef?.current;
      if (el) {
        const s = call.remoteStreamRef?.current;
        if (s && el.srcObject !== s) el.srcObject = s;
        try { const p = el.play(); if (p?.catch) p.catch(() => { /* ignore */ }); } catch { /* ignore */ }
      }
    }
  }, [state, remoteAudioRef, call.remoteStreamRef]);

  // Always-rendered hidden audio element. This element MUST live at a stable
  // DOM position across all states — if React unmounts/remounts it during
  // transitions, `srcObject` (attached by useVoiceCall.ontrack) is lost and
  // neither party hears any audio.
  const RemoteAudio = (
    <audio
      key="voice-remote-audio-persistent"
      ref={remoteAudioRef}
      autoPlay
      data-testid="voice-remote-audio"
      style={{ position: "fixed", width: 0, height: 0, opacity: 0, pointerEvents: "none" }}
    />
  );

  const isIncoming = state === "ringing_in";
  const otherName = isIncoming
    ? (incoming?.caller_name || "متصل")
    : (state === "active" || state === "connecting" || state === "ringing_out" ? "الطرف الآخر" : "");
  const orderLabel = incoming?.order_code ? `طلب ${incoming.order_code}` : "مكالمة طلبية";

  return (
    <>
      {RemoteAudio}
      {state !== "idle" && (
        <div
          className="fixed inset-0 z-[200] bg-gradient-to-br from-[#1a2b6d] to-[#2547D0] text-white flex flex-col"
          data-testid="voice-call-modal"
          role="dialog"
          aria-live="polite"
        >
      <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
        {/* Avatar circle */}
        <div className="w-32 h-32 rounded-full bg-white/15 flex items-center justify-center mb-6 backdrop-blur-sm border-4 border-white/20 talabya-shadow-float">
          {isIncoming
            ? <PhoneIncoming size={54} className="text-white animate-pulse-dot"/>
            : <Phone size={54} className="text-white"/>}
        </div>

        <div className="text-sm text-white/70 mb-1" data-testid="voice-call-order">{orderLabel}</div>
        <div className="text-3xl font-black mb-2" style={{ fontFamily: "Cairo" }} data-testid="voice-call-name">
          {otherName}
        </div>
        <div className="text-base text-white/80" data-testid="voice-call-state">
          {stateLabel(state)}
        </div>
        {state === "active" && (
          <div className="text-2xl font-black mt-3 tabular-nums" dir="ltr" data-testid="voice-call-duration">
            {formatDuration(durationSec)}
          </div>
        )}
        {error && (
          <div className="mt-4 px-4 py-2 rounded-xl bg-red-500/20 border border-red-300/40 text-red-100 text-sm" data-testid="voice-call-error">
            {error}
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="pb-10 pt-4 flex items-center justify-center gap-6">
        {isIncoming ? (
          <>
            <button
              onClick={reject}
              className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center talabya-shadow-float"
              aria-label="رفض"
              data-testid="voice-reject-btn"
            >
              <PhoneOff size={24}/>
            </button>
            <button
              onClick={acceptIncoming}
              className="w-16 h-16 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center talabya-shadow-float animate-pulse"
              aria-label="قبول"
              data-testid="voice-accept-btn"
            >
              <Phone size={24}/>
            </button>
          </>
        ) : (
          <>
            <button
              onClick={toggleMute}
              disabled={state !== "active"}
              className={`w-14 h-14 rounded-full flex items-center justify-center border-2 ${muted ? "bg-white text-[#2547D0]" : "bg-white/10 border-white/30 text-white"} disabled:opacity-40`}
              aria-label={muted ? "إلغاء الكتم" : "كتم"}
              data-testid="voice-mute-btn"
            >
              {muted ? <MicOff size={22}/> : <Mic size={22}/>}
            </button>
            <button
              onClick={hangup}
              className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center talabya-shadow-float"
              aria-label="إنهاء"
              data-testid="voice-hangup-btn"
            >
              <PhoneOff size={24}/>
            </button>
          </>
        )}
      </div>
        </div>
      )}
    </>
  );
}
